/**
 * JSON-LD structured data for SEO and geo.
 * Uses canonical base URL and optional address/region for Organization and Service.
 */

import { getBaseUrl } from './sitemap.config';

export interface OrganizationSchemaOptions {
  name: string;
  description?: string;
  url?: string;
  logo?: string;
  /** Physical address: street, city, state, postalCode, country (short codes preferred) */
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  contactPoint?: {
    contactType?: string;
    email?: string;
    telephone?: string;
    url?: string;
  };
}

export function buildOrganizationSchema(
  options: OrganizationSchemaOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: options.name,
    url,
  };
  if (options.description) schema.description = options.description;
  if (options.logo) schema.logo = options.logo;
  if (options.address && Object.values(options.address).some(Boolean)) {
    schema.address = {
      '@type': 'PostalAddress',
      ...options.address,
    };
  }
  if (
    options.contactPoint &&
    Object.values(options.contactPoint).some(Boolean)
  ) {
    schema.contactPoint = {
      '@type': 'ContactPoint',
      ...options.contactPoint,
    };
  }
  return schema;
}

export interface ServiceSchemaOptions {
  name: string;
  description?: string;
  url?: string;
  /** Service area (e.g. country or region code) for geo targeting */
  areaServed?: string | string[];
}

export function buildServiceSchema(
  options: ServiceSchemaOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.name,
    url,
  };
  if (options.description) schema.description = options.description;
  if (options.areaServed) {
    schema.areaServed = Array.isArray(options.areaServed)
      ? options.areaServed.map((a) =>
          a.length === 2 ? { '@type': 'Country', name: a } : a
        )
      : { '@type': 'Country', name: options.areaServed };
  }
  return schema;
}

// ---------------------------------------------------------------------------
// GEO: FAQPage
// ---------------------------------------------------------------------------

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQSchemaOptions {
  questions: FAQItem[];
  url?: string;
}

export function buildFAQSchema(
  options: FAQSchemaOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    url,
    mainEntity: options.questions.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };
}

// ---------------------------------------------------------------------------
// GEO: WebPage
// ---------------------------------------------------------------------------

export interface WebPageSchemaOptions {
  name: string;
  description?: string;
  url?: string;
  /** Primary entity this page is about (Organization, HowTo, Service, FAQPage, etc.) */
  mainEntity?: Record<string, unknown>;
}

export function buildWebPageSchema(
  options: WebPageSchemaOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: options.name,
    url,
  };
  if (options.description) schema.description = options.description;
  if (options.mainEntity) {
    // Strip the top-level @context from the nested entity to avoid duplication
    const { '@context': _ctx, ...entityWithoutContext } =
      options.mainEntity as Record<string, unknown>;
    void _ctx;
    schema.mainEntity = entityWithoutContext;
  }
  return schema;
}

// ---------------------------------------------------------------------------
// GEO: Article (for policy / info pages)
// ---------------------------------------------------------------------------

export interface ArticleSchemaOptions {
  headline: string;
  description?: string;
  url?: string;
  /** ISO 8601 date string, e.g. "2025-12-01" */
  datePublished: string;
  /** ISO 8601 date string */
  dateModified?: string;
  /** Publisher Organization schema object (or minimal {name,url}) */
  publisher?: Record<string, unknown>;
}

export function buildArticleSchema(
  options: ArticleSchemaOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: options.headline,
    url,
    datePublished: options.datePublished,
  };
  if (options.description) schema.description = options.description;
  if (options.dateModified) schema.dateModified = options.dateModified;
  if (options.publisher) {
    const { '@context': _ctx, ...pub } = options.publisher as Record<
      string,
      unknown
    >;
    void _ctx;
    schema.publisher = pub;
  }
  return schema;
}

// ---------------------------------------------------------------------------
// GEO: LocalBusiness
// ---------------------------------------------------------------------------

export interface LocalBusinessOptions {
  name: string;
  description?: string;
  url?: string;
  logo?: string;
  telephone?: string;
  email?: string;
  streetAddress: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: number;
  longitude: number;
  openingHours?: string[];
  paymentAccepted?: string[];
  priceRange?: string;
  sameAs?: string[];
}

export function buildLocalBusinessSchema(
  options: LocalBusinessOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: options.name,
    url,
    address: {
      '@type': 'PostalAddress',
      streetAddress: options.streetAddress,
      addressLocality: options.city,
      addressRegion: options.state,
      postalCode: options.postalCode,
      addressCountry: options.country,
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: options.latitude,
      longitude: options.longitude,
    },
  };
  if (options.description) schema.description = options.description;
  if (options.logo) schema.logo = options.logo;
  if (options.telephone) schema.telephone = options.telephone;
  if (options.email) schema.email = options.email;
  if (options.openingHours?.length)
    schema.openingHoursSpecification = options.openingHours.map((h) => ({
      '@type': 'OpeningHoursSpecification',
      hours: h,
    }));
  if (options.paymentAccepted?.length)
    schema.paymentAccepted = options.paymentAccepted.join(', ');
  if (options.priceRange) schema.priceRange = options.priceRange;
  if (options.sameAs?.length) schema.sameAs = options.sameAs;
  return schema;
}

// ---------------------------------------------------------------------------
// GEO: BreadcrumbList
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function buildBreadcrumbSchema(
  items: BreadcrumbItem[]
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

// ---------------------------------------------------------------------------
// GEO: Review + AggregateRating
// ---------------------------------------------------------------------------

export interface ReviewItem {
  author: string;
  reviewBody?: string;
  reviewRating: number;
  datePublished: string;
}

export interface ReviewSchemaOptions {
  itemReviewed: string;
  reviewCount: number;
  ratingValue: number;
  bestRating?: number;
  worstRating?: number;
  reviews?: ReviewItem[];
  url?: string;
}

export function buildReviewSchema(
  options: ReviewSchemaOptions
): Record<string, unknown> {
  const baseUrl = getBaseUrl();
  const url = options.url || baseUrl;
  const bestRating = options.bestRating ?? 5;
  const worstRating = options.worstRating ?? 1;
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: options.itemReviewed,
    url,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: options.ratingValue,
      reviewCount: options.reviewCount,
      bestRating,
      worstRating,
    },
  };
  if (options.reviews?.length) {
    schema.review = options.reviews.map((r) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: r.author },
      reviewBody: r.reviewBody || '',
      reviewRating: {
        '@type': 'Rating',
        ratingValue: r.reviewRating,
        bestRating,
        worstRating,
      },
      datePublished: r.datePublished,
    }));
  }
  return schema;
}

// ---------------------------------------------------------------------------
// GEO: Person (for provider profiles)
// ---------------------------------------------------------------------------

export interface PersonSchemaOptions {
  name: string;
  description?: string;
  url?: string;
  image?: string;
  jobTitle?: string;
  sameAs?: string[];
  address?: { city: string; state: string; country: string };
}

export function buildPersonSchema(
  options: PersonSchemaOptions
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: options.name,
    url: options.url || getBaseUrl(),
  };
  if (options.description) schema.description = options.description;
  if (options.image) schema.image = options.image;
  if (options.jobTitle) schema.jobTitle = options.jobTitle;
  if (options.sameAs?.length) schema.sameAs = options.sameAs;
  if (options.address) {
    schema.address = {
      '@type': 'PostalAddress',
      addressLocality: options.address.city,
      addressRegion: options.address.state,
      addressCountry: options.address.country,
    };
  }
  return schema;
}

// ---------------------------------------------------------------------------
// GEO: ItemList (for service listing pages)
// ---------------------------------------------------------------------------

export interface ItemListItem {
  name: string;
  url: string;
  position: number;
  image?: string;
  description?: string;
}

export function buildItemListSchema(
  items: ItemListItem[],
  listName: string,
  listUrl?: string
): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    ...(listUrl ? { url: listUrl } : {}),
    numberOfItems: items.length,
    itemListElement: items.map((item) => ({
      '@type': 'ListItem',
      position: item.position,
      name: item.name,
      url: item.url,
      ...(item.image ? { image: item.image } : {}),
      ...(item.description ? { description: item.description } : {}),
    })),
  };
}
